import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { connect, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { useTheme } from "../../theming";
import {
  createQueueRecord as createQueueRecordAction,
  updateQueueRecord as updateQueueRecordAction,
  deleteQueueRecord as deleteQueueRecordAction,
  getQueueRecord as getQueueRecordAction,
  listQueueRecords as listQueueRecordsAction,
} from "../../actions/queueServicesActions";
import {
  createNewsGenerationRecord as createNewsGenerationRecordAction,
  updateNewsGenerationRecord as updateNewsGenerationRecordAction,
  deleteNewsGenerationRecord as deleteNewsGenerationRecordAction,
  getNewsGenerationRecord as getNewsGenerationRecordAction,
  listNewsGenerationRecords as listNewsGenerationRecordsAction,
} from "../../actions/newsGenerationActions";
import {
  executeN8nWorkflow as executeN8nWorkflowAction,
  listN8nWorkflows as listN8nWorkflowsAction,
} from "../../actions/n8nWorkflowActions";
import { executeAiAutomationWorkflow as executeAiAutomationWorkflowAction } from "../../actions/aiAutomationActions";
import {
  cannedSearchCriteria,
  enhanceQueueRecordInput,
  validateCannedCriteria,
} from "./cannedSearchCriteria";
import {
  cannedNewsCriteria,
  enhanceNewsQueueRecordInput,
  validateCannedNewsCriteria,
} from "./cannedNewsCriteria";
import {
  enhanceLeadSearchQueueRecord,
  validateQueueRecord,
  logEnhancedQueueRecord,
  filterForGraphQLApi,
  logFilteredQueueRecord,
  createSimplifiedDataFormat,
  createMinimalDataFormat,
} from "./queueRecordEnhancer";
import { testApiConnection } from "../../apis/queueServicesApi";

const SalesProcessContext = createContext();

export const useSalesProcess = () => {
  const context = useContext(SalesProcessContext);
  if (!context) {
    throw new Error(
      "useSalesProcess must be used within a SalesProcessProvider"
    );
  }
  return context;
};

const SalesProcessProvider = ({
  children,
  // Queue Services Actions
  createQueueRecord,
  updateQueueRecord,
  deleteQueueRecord,
  getQueueRecord,
  listQueueRecords,
  // Queue Services State
  queueRecords,
  currentQueueRecord,
  queueRecordsConnection,
  queueServicesLoading,
  queueServicesError,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // Debug logging to see what we're receiving
  console.log(
    "SalesProcessProvider received createQueueRecord:",
    typeof createQueueRecord
  );
  console.log(
    "SalesProcessProvider createQueueRecord value:",
    createQueueRecord
  );

  const [currentScreen, setCurrentScreen] = useState(null);
  const [screenConfigs, setScreenConfigs] = useState({});
  const [userMode, setUserMode] = useState("default");
  const [activeWorkflow, setActiveWorkflow] = useState(null);
  const [startupName, setStartupName] = useState("");

  useEffect(() => {
    const storedStartupName = localStorage.getItem("STARTUP");
    if (storedStartupName) {
      setStartupName(storedStartupName);
      console.log(
        `PMM - SF - SalesProcessProvider.jsx - useEffect[] - storedStartupName is: ${JSON.stringify(
          storedStartupName
        )}`
      );
    }
  }, []);

  // Memoize loadScreenConfig to prevent infinite re-render loops
  const loadScreenConfig = useCallback(
    async (screenId) => {
      const config =
        screenConfigs[screenId] || getDefaultScreenConfig(screenId);
      setCurrentScreen(config);
      return config;
    },
    [screenConfigs]
  );

  const getDefaultScreenConfig = (screenId) => {
    const defaultConfigs = {
      "lead-iq": {
        id: "lead-iq",
        name: "Lead IQ - Initial Qualification",
        description: "Search and qualify leads using Apollo.io criteria",
        type: "search-form",
        sections: [
          {
            id: "search-criteria",
            type: "form-section",
            title: "Search Criteria",
            fields: [
              {
                id: "company-size",
                type: "select",
                label: "Company Size",
                options: [
                  { value: "1-10", label: "1-10 employees" },
                  { value: "11-50", label: "11-50 employees" },
                  { value: "51-200", label: "51-200 employees" },
                  { value: "201-500", label: "201-500 employees" },
                  { value: "501-1000", label: "501-1000 employees" },
                  { value: "1001+", label: "1001+ employees" },
                ],
                required: true,
              },
              {
                id: "industry",
                type: "multi-select",
                label: "Industry",
                options: [
                  { value: "technology", label: "Technology" },
                  { value: "healthcare", label: "Healthcare" },
                  { value: "finance", label: "Finance" },
                  { value: "education", label: "Education" },
                  { value: "retail", label: "Retail" },
                  { value: "manufacturing", label: "Manufacturing" },
                ],
                required: true,
              },
              {
                id: "job-title",
                type: "text",
                label: "Job Title Keywords",
                placeholder: "e.g., CEO, CTO, VP Engineering",
                required: false,
              },
              {
                id: "location",
                type: "text",
                label: "Location",
                placeholder: "e.g., San Francisco, CA",
                required: false,
              },
              {
                id: "technologies",
                type: "multi-select",
                label: "Technologies Used",
                options: [
                  { value: "react", label: "React" },
                  { value: "node", label: "Node.js" },
                  { value: "python", label: "Python" },
                  { value: "aws", label: "AWS" },
                  { value: "azure", label: "Azure" },
                  { value: "gcp", label: "Google Cloud" },
                ],
                required: false,
              },
            ],
          },
          {
            id: "qualification-criteria",
            type: "form-section",
            title: "Qualification Criteria",
            fields: [
              {
                id: "budget-range",
                type: "select",
                label: "Budget Range",
                options: [
                  { value: "0-10k", label: "$0 - $10K" },
                  { value: "10k-50k", label: "$10K - $50K" },
                  { value: "50k-100k", label: "$50K - $100K" },
                  { value: "100k+", label: "$100K+" },
                ],
                required: true,
              },
              {
                id: "timeline",
                type: "select",
                label: "Implementation Timeline",
                options: [
                  { value: "immediate", label: "Immediate (0-3 months)" },
                  { value: "short-term", label: "Short-term (3-6 months)" },
                  { value: "long-term", label: "Long-term (6+ months)" },
                ],
                required: true,
              },
              {
                id: "decision-maker",
                type: "checkbox",
                label: "Must be decision maker",
                required: false,
              },
            ],
          },
        ],
        actions: [
          {
            id: "search-leads",
            type: "primary",
            label: "Search Leads",
            action: "queue-lead-search",
          },
          {
            id: "save-criteria",
            type: "secondary",
            label: "Save Criteria",
            action: "save-search",
          },
        ],
      },
      "lead-search-queue": {
        id: "lead-search-queue",
        name: "Lead Search Queue",
        description:
          "Queue up Apollo.io lead search criteria for async processing.",
        type: "queue-form",
        sections: [
          {
            id: "canned-search-criteria",
            type: "custom-section",
            title: "Canned Search Criteria",
            cannedCriteria: cannedSearchCriteria,
          },
        ],
        actions: [
          {
            id: "queue-canned-search",
            type: "primary",
            label: "Initiate Selected Search",
            action: "queue-lead-search",
          },
          {
            id: "view-queue-status",
            type: "secondary",
            label: "View Queue Status",
            action: "list-queue-records",
          },
        ],
      },
      "news-generation-queue": {
        id: "news-generation-queue",
        name: "Research Generation Queue",
        description:
          "Initiate AI-powered research requests with configurable search criteria and report parameters.",
        type: "queue-form",
        sections: [
          {
            id: "canned-news-criteria",
            type: "custom-section",
            title: "Research Configurations",
            cannedCriteria: cannedNewsCriteria,
          },
        ],
        actions: [
          {
            id: "queue-canned-news",
            type: "primary",
            label: "Initiate Research Generation",
            action: "queue-news-generation",
          },
          {
            id: "view-news-queue-status",
            type: "secondary",
            label: "View Queue Status",
            action: "list-queue-records",
          },
        ],
      },
      "workflow-builder": {
        id: "workflow-builder",
        name: "Workflow Builder",
        description:
          "Select and initiate saved workflows from your workflow library.",
        type: "workflow-form",
        sections: [
          {
            id: "workflow-selection",
            type: "custom-section",
            title: "Workflow Selection",
            component: "WorkflowBuilder",
          },
        ],
        actions: [
          {
            id: "initiate-workflow",
            type: "primary",
            label: "Initiate Workflow",
            action: "execute-workflow",
          },
          {
            id: "view-workflow-status",
            type: "secondary",
            label: "View Execution Status",
            action: "list-workflow-executions",
          },
        ],
      },
    };

    return defaultConfigs[screenId] || null;
  };

  const executeAction = async (actionId, data) => {
    console.log(
      `PMM - SF - SalesProcessProvider.jsx - actionId is: ${JSON.stringify(
        actionId
      )}`
    );
    switch (actionId) {
      case "search-apollo":
        // This would trigger your backend workflow
        console.log("Searching Apollo.io with criteria:", data);
        return { success: true, message: "Search initiated" };

      case "save-search":
        // Save search criteria to DynamoDB
        console.log("Saving search criteria:", data);
        return { success: true, message: "Search criteria saved" };

      case "queue-lead-search":
        // Queue lead search using queue services
        console.log("Queueing lead search:", data);
        try {
          // Validate the incoming data
          validateCannedCriteria(data);

          // Create base queue record input
          const baseQueueRecordInput = {
            name: `Lead Search - ${data.jobTitle || "General"}`,
            status: "pending",
            data: JSON.stringify(data),
          };

          // ENHANCEMENT POINT: Add tenant ID, entity type, and other metadata
          // This is where you can easily modify or add additional fields to the queue record
          const enhancedQueueRecordInput = enhanceLeadSearchQueueRecord(
            baseQueueRecordInput,
            startupName,
            userMode,
            {
              // Add any additional context here
              searchCriteria: data,
              screenId: currentScreen?.id,
            }
          );

          // Validate the enhanced input
          try {
            validateQueueRecord(enhancedQueueRecordInput, "enhanced");
            console.log("✅ Enhanced record validation passed");
          } catch (error) {
            console.error(
              "❌ Enhanced record validation failed:",
              error.message
            );
            return {
              success: false,
              message: "Enhanced record validation failed: " + error.message,
            };
          }

          // Log the enhanced input for debugging
          logEnhancedQueueRecord(enhancedQueueRecordInput, "queue-lead-search");

          // Filter the enhanced input to be compatible with the GraphQL API
          const filteredQueueRecordInput = filterForGraphQLApi(
            enhancedQueueRecordInput
          );

          // Log the filtered input for debugging
          logFilteredQueueRecord(filteredQueueRecordInput, "queue-lead-search");

          // Validate that the filtered input matches the GraphQL schema
          try {
            validateQueueRecord(filteredQueueRecordInput, "filtered");
            console.log("✅ GraphQL Schema validation passed");
          } catch (error) {
            console.error(
              "❌ GraphQL Schema validation failed:",
              error.message
            );
            return {
              success: false,
              message: "Schema validation failed: " + error.message,
            };
          }

          // Try simplified format if Lambda is having issues
          const simplifiedQueueRecordInput = createSimplifiedDataFormat(
            enhancedQueueRecordInput
          );
          console.log(
            "🔄 Simplified format created as backup:",
            simplifiedQueueRecordInput
          );

          // Create minimal format as ultimate fallback
          const minimalQueueRecordInput = createMinimalDataFormat(
            enhancedQueueRecordInput
          );
          console.log(
            "🔄 Minimal format created as fallback:",
            minimalQueueRecordInput
          );

          // Dispatch the Redux action with filtered input
          const action = createQueueRecordAction(filteredQueueRecordInput);
          console.log("Action to dispatch:", action);
          dispatch(action);
          console.log("Action dispatched successfully");
          return { success: true, message: "Lead search queued successfully" };
        } catch (error) {
          console.error("Error in queue-lead-search:", error);
          return {
            success: false,
            message: "Failed to queue search: " + error.message,
          };
        }

      case "queue-news-generation":
        // Queue research generation using research configuration API
        console.log("Queueing research generation:", data);
        try {
          // Validate the incoming data
          validateCannedNewsCriteria(data);

          // Create research request input
          const researchRequestInput = {
            title: `Research Generation - ${
              data.researchRequest?.searchQuery || "General"
            }`,
            content: JSON.stringify(data),
            source: "sales-process-dashboard",
            url: null,
            publishedDate: new Date().toISOString(),
            tenantId: startupName || "default",
          };

          // Create enhanced research request input with additional metadata
          const enhancedResearchRequestInput = {
            ...researchRequestInput,
            // Add additional metadata to content
            content: JSON.stringify({
              ...data,
              tenantId: startupName || "default",
              userMode,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              source: "sales-process-dashboard",
              sourceScreen: "news-generation-queue",
              businessContext: {
                startupName,
                userMode,
                reportType: "research-generation",
                expectedOutcome: "research-report",
                entityType: "NEWSGENMGR",
              },
            }),
          };

          // Log the enhanced input for debugging
          console.log(
            "🚀 Enhanced Research Request Input for queue-news-generation"
          );
          console.log("📋 Full Input:", enhancedResearchRequestInput);
          console.log(
            `PMM - SF - enhanced research msg is: ${JSON.stringify(
              enhancedResearchRequestInput
            )}`
          );

          // Dispatch the Redux action with research request input
          const action = createNewsGenerationRecordAction(
            enhancedResearchRequestInput
          );
          console.log("Action to dispatch:", action);
          dispatch(action);
          console.log("Action dispatched successfully");
          return {
            success: true,
            message: "Research generation queued successfully",
          };
        } catch (error) {
          console.error("Error in queue-news-generation:", error);
          return {
            success: false,
            message: "Failed to queue research generation: " + error.message,
          };
        }

      case "get-queue-status":
        // Get queue record status
        console.log("Getting queue status for:", data.id);
        dispatch(getQueueRecordAction(data.id));
        return { success: true, message: "Queue status retrieved" };

      case "list-queue-records":
        // List all queue records
        console.log("Listing queue records");
        dispatch(listQueueRecordsAction(data.limit, data.nextToken));
        return { success: true, message: "Queue records listed" };

      case "execute-workflow":
        // Execute workflow using AI Automation API
        console.log("Executing AI Automation workflow:", data);
        try {
          // Create AI Automation workflow execution input
          const workflowExecutionInput = {
            workflowId: data.workflowId,
            workflowName: data.workflowName || "Unknown Workflow",
            tenantId: startupName || "default",
            userMode,
            automationType: data.automationType || "workflow-execution",
            cloudTarget: data.cloudTarget || "aws",
            executionContext: {
              source: "sales-process-dashboard",
              sourceScreen: "workflow-builder",
              businessContext: {
                startupName,
                userMode,
                workflowType: "automation",
                expectedOutcome: "workflow-execution",
                cloudTarget: data.cloudTarget || "aws",
              },
            },
            timestamp: new Date().toISOString(),
          };

          console.log(
            "🚀 AI Automation Workflow Execution Input:",
            workflowExecutionInput
          );
          console.log(
            `PMM - SF - AI Automation workflow execution msg is: ${JSON.stringify(
              workflowExecutionInput
            )}`
          );

          // Dispatch the Redux action with AI Automation workflow execution input
          const action = executeAiAutomationWorkflowAction(
            workflowExecutionInput
          );
          console.log("Action to dispatch:", action);
          dispatch(action);
          console.log("Action dispatched successfully");

          return {
            success: true,
            message: `AI Automation Workflow "${
              data.workflowName
            }" execution initiated successfully for ${(
              data.cloudTarget || "aws"
            ).toUpperCase()}`,
          };
        } catch (error) {
          console.error("Error in execute-workflow:", error);
          return {
            success: false,
            message:
              "Failed to execute AI Automation workflow: " + error.message,
          };
        }

      case "list-workflow-executions":
        // List workflow executions
        console.log("Listing workflow executions");
        // TODO: Implement workflow execution listing
        return { success: true, message: "Workflow executions listed" };

      default:
        console.warn("Unknown action:", actionId);
        return { success: false, message: "Unknown action" };
    }
  };

  const value = {
    currentScreen,
    screenConfigs,
    userMode,
    activeWorkflow,
    startupName,
    loadScreenConfig,
    setCurrentScreen,
    setUserMode,
    setActiveWorkflow,
    executeAction,
    theme,
    // Queue Services State
    queueRecords,
    currentQueueRecord,
    queueRecordsConnection,
    queueServicesLoading,
    queueServicesError,
    // Queue Services Actions
    createQueueRecord,
    updateQueueRecord,
    deleteQueueRecord,
    getQueueRecord,
    listQueueRecords,
    // Debug functions
    testApiConnection,
  };

  return (
    <SalesProcessContext.Provider value={value}>
      {children}
    </SalesProcessContext.Provider>
  );
};

// PropTypes Definition
SalesProcessProvider.propTypes = {
  children: PropTypes.node.isRequired,
  // Queue Services Actions
  createQueueRecord: PropTypes.func.isRequired,
  updateQueueRecord: PropTypes.func.isRequired,
  deleteQueueRecord: PropTypes.func.isRequired,
  getQueueRecord: PropTypes.func.isRequired,
  listQueueRecords: PropTypes.func.isRequired,
  // Queue Services State
  queueRecords: PropTypes.array,
  currentQueueRecord: PropTypes.object,
  queueRecordsConnection: PropTypes.object,
  queueServicesLoading: PropTypes.bool,
  queueServicesError: PropTypes.string,
};

const mapStateToProps = (state) => ({
  queueRecords: state?.queueServices?.queueRecords || [],
  currentQueueRecord: state?.queueServices?.currentQueueRecord || null,
  queueRecordsConnection: state?.queueServices?.queueRecordsConnection || null,
  queueServicesLoading: state?.queueServices?.loading || false,
  queueServicesError: state?.queueServices?.error || null,
});

const mapDispatchToProps = (dispatch) => ({
  createQueueRecord: (input) => dispatch(createQueueRecordAction(input)),
  updateQueueRecord: (input) => dispatch(updateQueueRecordAction(input)),
  deleteQueueRecord: (id) => dispatch(deleteQueueRecordAction(id)),
  getQueueRecord: (id) => dispatch(getQueueRecordAction(id)),
  listQueueRecords: (limit, nextToken) =>
    dispatch(listQueueRecordsAction(limit, nextToken)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SalesProcessProvider);

// Add named export for the unconnected component
export { SalesProcessProvider };
